import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from "@/components/ui/button";
import { ChevronRight, Camera, Plane, Users, Video, Sparkles, X, Check, Loader2, ArrowRight, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import { OrderServiceCTA } from '../components/OrderServiceCTA';
import SEO, { getLocalBusinessSchema, getOrganizationSchema } from '../components/SEO';
import PhonePopover from '../components/PhonePopover';
import ResponsiveListingImage from '../components/ResponsiveListingImage';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || '';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SERVICE CONSULTATION MODAL - Reusable across all homepage service sections
 * ═══════════════════════════════════════════════════════════════════════════
 */
const ConsultationModal = ({ isOpen, onClose, source, subject, serviceLabel }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    preferredContact: ''
  });

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: subject,
          message: `Service Interest: ${serviceLabel}\nPreferred Contact: ${formData.preferredContact || 'Email'}\nPhone: ${formData.phone || 'Not provided'}\n\n${formData.message}`,
          source: source,
          phone: formData.phone || null,
          preferred_contact: formData.preferredContact || 'email'
        })
      });
      
      if (response.status === 429) {
        throw new Error('Too many requests. Please wait a few minutes.');
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit');
      }
      
      setIsSubmitted(true);
    } catch (error) {
      setSubmitError(error.message || 'Something went wrong.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      message: '',
      preferredContact: ''
    });
    setIsSubmitted(false);
    setSubmitError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto border border-black/10 shadow-2xl">
        {/* Close button */}
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Close modal"
          data-testid="consultation-modal-close"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        {isSubmitted ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-[#C6A14A]/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-[#C6A14A]" />
            </div>
            <h3 className="font-serif text-2xl text-gray-900 mb-3">Thank You</h3>
            <p className="text-gray-600 mb-6">Your consultation request has been received. We'll be in touch within 1 business day.</p>
            <button 
              onClick={handleClose} 
              className="px-8 py-3 bg-[#C6A14A] text-white rounded-xl font-semibold hover:bg-[#b5923f] transition-colors"
              data-testid="consultation-modal-success-close"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="text-center mb-6 pr-8">
              <h2 className="font-serif text-2xl text-gray-900 mb-2">Request a Consultation</h2>
              <p className="text-gray-500 text-sm">{serviceLabel} — We'll reply within 1 business day.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="consultation-modal-form">
              {/* Name */}
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-[#F7F7F5] border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#C6A14A] focus:ring-1 focus:ring-[#C6A14A]/20 transition-colors"
                  placeholder="Your name"
                  data-testid="consultation-modal-name"
                />
              </div>
              
              {/* Email */}
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-[#F7F7F5] border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#C6A14A] focus:ring-1 focus:ring-[#C6A14A]/20 transition-colors"
                  placeholder="you@email.com"
                  data-testid="consultation-modal-email"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-[#F7F7F5] border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#C6A14A] focus:ring-1 focus:ring-[#C6A14A]/20 transition-colors"
                  placeholder="(555) 123-4567"
                  data-testid="consultation-modal-phone"
                />
              </div>

              {/* Preferred Contact Method */}
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Preferred Contact Method</label>
                <div className="flex gap-4">
                  {['Email', 'Phone', 'Text'].map((method) => (
                    <label key={method} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="preferredContact"
                        value={method.toLowerCase()}
                        checked={formData.preferredContact === method.toLowerCase()}
                        onChange={(e) => handleInputChange('preferredContact', e.target.value)}
                        className="w-4 h-4 text-[#C6A14A] focus:ring-[#C6A14A]"
                      />
                      <span className="text-gray-700 text-sm">{method}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Message *</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  required
                  rows={4}
                  className="w-full px-4 py-3 bg-[#F7F7F5] border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#C6A14A] focus:ring-1 focus:ring-[#C6A14A]/20 transition-colors resize-none"
                  placeholder="Tell us about your project..."
                  data-testid="consultation-modal-message"
                />
              </div>

              {/* Error Message */}
              {submitError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  {submitError}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 bg-[#C6A14A] text-white rounded-xl font-semibold hover:bg-[#b5923f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                data-testid="consultation-modal-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Request'
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * HERO SLIDESHOW COMPONENT - Rotating background images
 * ═══════════════════════════════════════════════════════════════════════════
 */
const HERO_IMAGES = [
  {
    src: "https://customer-assets.emergentagent.com/job_page-builder-81/artifacts/q25kwdyr_Adero_Canyon_Catteau_BKElevation_3301.jpg",
    alt: "Luxury real estate exterior photography"
  },
  {
    src: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&q=80&w=2000",
    alt: "Modern architecture exterior"
  },
  {
    src: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&q=80&w=2000",
    alt: "Luxury interior living space"
  },
  {
    src: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&q=80&w=2000",
    alt: "Desert modern home"
  }
];

const HeroSlideshow = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [imagesReady, setImagesReady] = useState(false);
  const imageRefs = useRef([]);
  const intervalRef = useRef(null);
  
  // Check for reduced motion preference
  const prefersReducedMotion = typeof window !== 'undefined' && 
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  // Preload all images on mount
  useEffect(() => {
    let mounted = true;
    
    const preloadImages = async () => {
      const promises = HERO_IMAGES.map((image) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.src = image.src;
          img.onload = async () => {
            // Use decode() for smoother rendering if supported
            if (img.decode) {
              try {
                await img.decode();
              } catch (e) {
                // decode() failed, but image is still loaded
              }
            }
            resolve();
          };
          img.onerror = () => resolve(); // Don't block on errors
        });
      });
      
      await Promise.all(promises);
      if (mounted) {
        setImagesReady(true);
      }
    };
    
    preloadImages();
    return () => { mounted = false; };
  }, []);

  // Auto-rotation interval
  useEffect(() => {
    if (prefersReducedMotion || !imagesReady) return;
    
    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 7000); // 7 seconds per slide
    
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [prefersReducedMotion, imagesReady]);

  return (
    <div 
      className="absolute inset-0 overflow-hidden bg-[#0f1724]"
      style={{ contain: 'paint' }}
    >
      {/* Render ALL images as stacked layers - no remounting */}
      {HERO_IMAGES.map((image, index) => (
        <div
          key={image.src} // Stable key - never changes
          ref={(el) => (imageRefs.current[index] = el)}
          className="absolute inset-0"
          style={{
            opacity: index === activeIndex ? 1 : 0,
            transition: prefersReducedMotion ? 'none' : 'opacity 1.5s ease-in-out',
            willChange: 'opacity',
            zIndex: index === activeIndex ? 2 : 1,
          }}
        >
          <img
            src={image.src}
            alt={image.alt}
            className="w-full h-full object-cover"
            style={{
              transform: !prefersReducedMotion ? 'scale(1.02)' : 'none',
              willChange: 'transform',
            }}
            loading="eager"
            decoding="async"
          />
        </div>
      ))}
    </div>
  );
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * HOME PAGE COMPONENT
 * ═══════════════════════════════════════════════════════════════════════════
 */

const Home = () => {
  // Modal state for service consultations
  const [activeModal, setActiveModal] = useState(null);
  
  // Modal configuration for each service
  const modalConfigs = {
    digital_persona: {
      source: 'homepage_digital_persona',
      subject: 'Digital Persona Consultation',
      serviceLabel: 'Digital Persona Development'
    },
    real_estate: {
      source: 'real_estate_homepage',
      subject: 'Real Estate Photography Consultation',
      serviceLabel: 'Signature Real Estate Photography'
    },
    aerial: {
      source: 'aerial_homepage',
      subject: 'Aerial & Drone Media Consultation',
      serviceLabel: 'Aerial & Drone Media'
    },
    headshots: {
      source: 'headshots_homepage',
      subject: 'Headshots & Portraits Consultation',
      serviceLabel: 'Professional Headshots & Portraits'
    },
    video_tours: {
      source: 'video_tours_homepage',
      subject: 'Video Tours Consultation',
      serviceLabel: 'Cinematic Video Tours'
    },
    restoration: {
      source: 'restoration_homepage',
      subject: 'Photo Restoration Consultation',
      serviceLabel: 'Photo Restoration & Enhancement'
    }
  };

  // Fade-in-on-scroll effect
  const observerRef = useRef(null);
  
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    
    const elements = document.querySelectorAll('.fade-on-scroll');
    elements.forEach((el) => observerRef.current.observe(el));
    
    return () => observerRef.current?.disconnect();
  }, []);

  // Photo Restoration slider images
  const restorationImages = {
    before: "https://customer-assets.emergentagent.com/job_e9a01186-23de-4e11-a93b-b3b28ef3cbe6/artifacts/7nc6bd44_Scan.png",
    after: "https://customer-assets.emergentagent.com/job_e9a01186-23de-4e11-a93b-b3b28ef3cbe6/artifacts/t777a08b_ChatGPT%20Image%20Feb%2012%2C%202026%2C%2001_02_46%20PM.png"
  };

  // Homepage-specific JSON-LD structured data
  const homepageJsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "RC Studios",
    "url": "https://rcstudios.net",
    "description": "Professional real estate photography serving Kingman, Bullhead City, and Lake Havasu. HDR listing photos, drone aerial photography, video tours, and 24-hour turnaround.",
    "areaServed": [
      { "@type": "City", "name": "Kingman, Arizona" },
      { "@type": "City", "name": "Bullhead City, Arizona" },
      { "@type": "City", "name": "Lake Havasu City, Arizona" },
      { "@type": "AdministrativeArea", "name": "Mohave County, Arizona" }
    ],
    "serviceOffered": [
      { "@type": "Service", "name": "Real Estate Photography" },
      { "@type": "Service", "name": "Drone & Aerial Photography" },
      { "@type": "Service", "name": "Video Tours" },
      { "@type": "Service", "name": "Virtual Twilight" }
    ]
  };

  // Real listing photos by Gregory - with descriptive alt text
  const staticListings = [
    { 
      id: 1, 
      src: "https://customer-assets-lqy194kg.emergentagent.net/job_42e529fb-0c64-4c59-a1a2-25a0eb5bcf7b/artifacts/6759d112ad9a8611_listing-kitchen-1.jpg",
      alt: "Kingman AZ real estate photo - remodeled kitchen with marble countertops and island seating"
    },
    { 
      id: 2, 
      src: "https://customer-assets-lqy194kg.emergentagent.net/job_42e529fb-0c64-4c59-a1a2-25a0eb5bcf7b/artifacts/341202cc916b8183_listing-kitchen-2.jpg",
      alt: "Kingman AZ real estate photo - modern kitchen with stainless steel appliances and natural light"
    },
    { 
      id: 3, 
      src: "https://customer-assets-lqy194kg.emergentagent.net/job_42e529fb-0c64-4c59-a1a2-25a0eb5bcf7b/artifacts/1d26279be37d4f9e_listing-living-1.jpg",
      alt: "Kingman AZ real estate photo - spacious living room with vaulted ceilings and ceiling fan"
    },
    { 
      id: 4, 
      src: "https://customer-assets-lqy194kg.emergentagent.net/job_42e529fb-0c64-4c59-a1a2-25a0eb5bcf7b/artifacts/54f169e18459603c_listing-bath-1.jpg",
      alt: "Kingman AZ real estate photo - updated bathroom with hexagon tile shower and marble vanity"
    },
    { 
      id: 5, 
      src: "https://customer-assets-lqy194kg.emergentagent.net/job_42e529fb-0c64-4c59-a1a2-25a0eb5bcf7b/artifacts/c1b17c656e2e780b_listing-bath-2.jpg",
      alt: "Kingman AZ real estate photo - primary bathroom with double vanity and walk-in shower"
    },
  ];

  // Fetch portfolio items for Recent Listings
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [portfolioLoading, setPortfolioLoading] = useState(true);

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        const response = await fetch(`${API_URL}/api/portfolio/public?limit=6`);
        if (response.ok) {
          const data = await response.json();
          setPortfolioItems(data);
        }
      } catch (err) {
        console.error('Failed to fetch portfolio:', err);
      } finally {
        setPortfolioLoading(false);
      }
    };
    fetchPortfolio();
  }, []);

  // Use portfolio items if available, otherwise fall back to static listings
  // Static listings link to /portfolio since they don't have individual galleries
  const recentListings = portfolioItems.length > 0 
    ? portfolioItems.map(item => ({
        id: item.id,
        src: item.cover_url,
        alt: `${item.address} - ${item.city} AZ real estate photography`,
        address: item.address,
        city: item.city,
        gallery_slug: item.gallery_slug,
        photo_count: item.photo_count,
        hasRealGallery: true,
      }))
    : staticListings.map(item => ({
        ...item,
        hasRealGallery: false,
      }));

  return (
    <div className="flex flex-col">
      {/* SEO Meta Tags & Structured Data - Homepage Specific */}
      <Helmet>
        {/* Title */}
        <title>Real Estate Photography in Kingman, Bullhead City & Lake Havasu | RC Studios</title>
        
        {/* Meta Description */}
        <meta name="description" content="Professional real estate photography for Kingman, Bullhead City, and Lake Havasu. HDR listing photos, drone aerials, video tours, and 24-hour turnaround. Packages from $199." />
        
        {/* Canonical */}
        <link rel="canonical" href="https://rcstudios.net/" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rcstudios.net/" />
        <meta property="og:title" content="Real Estate Photography from $199 | RC Studios Kingman AZ" />
        <meta property="og:description" content="HDR listing photos, drone aerials & video tours with 24-hour turnaround. Serving Kingman, Bullhead City & Lake Havasu, AZ. Packages from $199." />
        <meta property="og:image" content="https://rcstudios.net/og-default-v3.jpg?v=3" />
        <meta property="og:site_name" content="RC Studios" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://rcstudios.net/" />
        <meta name="twitter:title" content="Real Estate Photography from $199 | RC Studios Kingman AZ" />
        <meta name="twitter:description" content="HDR listing photos, drone aerials & video tours with 24-hour turnaround. Serving Kingman, Bullhead City & Lake Havasu, AZ. Packages from $199." />
        <meta name="twitter:image" content="https://rcstudios.net/og-default-v3.jpg?v=3" />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(homepageJsonLd)}
        </script>
      </Helmet>

      {/* ════════════════════════════════════════════════════════════════════
          HERO SECTION - Real Estate Photography Focus
          ════════════════════════════════════════════════════════════════════ */}
      <section className="hero-main relative min-h-[55vh] md:min-h-[60vh] w-full overflow-hidden">
        {/* Slideshow Component */}
        <HeroSlideshow />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/70 z-10"></div>
        
        {/* Hero Content */}
        <div className="hero-safe-top relative z-20 flex items-center justify-center min-h-[55vh] md:min-h-[60vh] pb-12">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h1 className="hero-title-mobile font-serif md:text-5xl lg:text-6xl text-white mb-4 leading-tight">
              Real Estate Photography for Kingman, Bullhead City & Lake Havasu
            </h1>
            <p className="text-white/90 text-lg md:text-xl max-w-2xl mx-auto mb-8 font-light">
              HDR listing photos, drone aerials, cinematic video tours — delivered within 24 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/book"
                data-testid="hero-book-shoot-btn"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg bg-[#C6A14A] text-white font-semibold text-lg tracking-wide hover:bg-[#b5923f] transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book a Shoot
                <ChevronRight className="w-5 h-5" />
              </Link>
              <PhonePopover variant="hero">
                Call (928) 530-6604
              </PhonePopover>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════════
          LUXURY BODY CONTAINER - Warm off-white editorial aesthetic
          ════════════════════════════════════════════════════════════════════ */}
      <div className="bg-[#F7F7F5]">
        
        {/* SEO H1 - Visually styled but semantically first */}
        <h1 className="sr-only">Real Estate Photography in Kingman, Bullhead City & Lake Havasu, Arizona</h1>

        {/* ════════════════════════════════════════════════════════════════════
            RECENT LISTINGS - Portfolio Grid (5 Real Photos)
            ════════════════════════════════════════════════════════════════════ */}
        <section className="pt-12 pb-8 md:pt-16 md:pb-12 px-6">
          <div className="max-w-[1200px] mx-auto">
            {/* Section Header */}
            <div className="text-center mb-8 fade-on-scroll opacity-0 translate-y-8 transition-all duration-700">
              <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.3em] uppercase mb-3">
                Our Work
              </p>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-gray-900 mb-3">
                Recent Listings
              </h2>
              <p className="text-gray-600 text-base max-w-xl mx-auto">
                A selection of properties we've photographed for agents across Mohave County.
              </p>
            </div>

            {/* 5-Image Grid - Responsive: 2 cols mobile, 3 cols md, special layout for 5 images */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {/* First 3 images in top row (full width on md+) */}
              {recentListings.slice(0, 3).map((listing) => (
                <Link 
                  key={listing.id}
                  to={listing.hasRealGallery && listing.gallery_slug ? `/showcase/${listing.gallery_slug}` : '/portfolio'}
                  className="aspect-[4/3] bg-gray-200 rounded-lg overflow-hidden relative group cursor-pointer hover:shadow-xl transition-all duration-300"
                  data-testid={`recent-listing-${listing.id}`}
                >
                  {listing.src ? (
                    <ResponsiveListingImage 
                      src={listing.src}
                      alt={listing.alt}
                      sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 400px"
                      className="transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                      <Camera className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  {/* Always-visible gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  {/* Always-visible address/city */}
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    {listing.address ? (
                      <>
                        <p className="text-white font-medium text-sm truncate">{listing.address}</p>
                        <p className="text-white/70 text-xs">{listing.city}</p>
                      </>
                    ) : (
                      <p className="text-white/70 text-xs">{listing.alt?.split(' - ')[0] || 'Kingman, AZ'}</p>
                    )}
                  </div>
                  {/* Hover cue */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-white/90 backdrop-blur-sm text-gray-900 px-4 py-2 rounded-lg text-sm font-medium shadow-lg">
                      View Gallery
                    </div>
                  </div>
                </Link>
              ))}
              
              {/* Last 2 images - centered in bottom row */}
              <div className="col-span-2 md:col-span-3 grid grid-cols-2 gap-3 md:gap-4 max-w-[800px] mx-auto">
                {recentListings.slice(3, 5).map((listing) => (
                  <Link 
                    key={listing.id}
                    to={listing.hasRealGallery && listing.gallery_slug ? `/showcase/${listing.gallery_slug}` : '/portfolio'}
                    className="aspect-[4/3] bg-gray-200 rounded-lg overflow-hidden relative group cursor-pointer hover:shadow-xl transition-all duration-300"
                    data-testid={`recent-listing-${listing.id}`}
                  >
                    {listing.src ? (
                      <ResponsiveListingImage 
                        src={listing.src}
                        alt={listing.alt}
                        sizes="(max-width: 640px) 50vw, (max-width: 1024px) 50vw, 400px"
                        className="transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                        <Camera className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                    {/* Always-visible gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    {/* Always-visible address/city */}
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      {listing.address ? (
                        <>
                          <p className="text-white font-medium text-sm truncate">{listing.address}</p>
                          <p className="text-white/70 text-xs">{listing.city}</p>
                        </>
                      ) : (
                        <p className="text-white/70 text-xs">{listing.alt?.split(' - ')[0] || 'Kingman, AZ'}</p>
                      )}
                    </div>
                    {/* Hover cue */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-white/90 backdrop-blur-sm text-gray-900 px-4 py-2 rounded-lg text-sm font-medium shadow-lg">
                        View Gallery
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="text-center mt-8">
              <Link 
                to="/portfolio"
                className="inline-flex items-center gap-2 text-[#C6A14A] hover:text-[#b5923f] font-medium transition-colors"
              >
                View All Portfolio
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════════════════════════
            GOOGLE REVIEWS SECTION
            ════════════════════════════════════════════════════════════════════ */}
        <section className="py-8 md:py-12 px-6">
          <div className="max-w-[900px] mx-auto">
            <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-8 md:p-10 text-center">
              {/* Stars */}
              <div className="flex items-center justify-center gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-6 h-6 fill-[#F5C056] text-[#F5C056]" />
                ))}
              </div>
              
              <h2 className="font-serif text-2xl md:text-3xl text-gray-900 mb-3">
                Trusted by Agents Across Mohave County
              </h2>
              <p className="text-gray-600 text-base mb-6 max-w-lg mx-auto">
                See what real estate professionals are saying about our photography services.
              </p>
              
              <a 
                href="https://www.google.com/maps/place/RCSTUDIOS+LLC/data=!4m2!3m1!1s0x44d30e85973566fd:0xe4c2141898983fcf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-xl bg-white border-2 border-gray-200 hover:border-[#C6A14A] text-gray-900 font-semibold transition-all duration-300 hover:shadow-lg"
                data-testid="google-reviews-btn"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                See Our Reviews on Google
              </a>
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════════════════════════
            INTRO TEXT SECTION - Real Estate Focus
            ════════════════════════════════════════════════════════════════════ */}
        <section className="relative py-8 md:py-12 px-6">
          <div className="max-w-[1100px] mx-auto">
            {/* Editorial Panel */}
            <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-6 md:p-10 text-center">
              <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.3em] uppercase mb-4 fade-on-scroll opacity-0 translate-y-8 transition-all duration-700">
                Premium Real Estate Media
              </p>
              <h2 className="font-serif text-3xl md:text-5xl text-gray-900 mb-4 fade-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-100">
                Luxury Property Photography & Marketing Media
              </h2>
              <p className="text-lg md:text-xl text-gray-600 font-light mb-6 fade-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-200">
                Let's bring your next listing to life.
              </p>
              
              <div className="flex flex-col items-center gap-2 fade-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-300">
                <Link 
                  to="/pricing/real-estate"
                  data-testid="hero-cta-quote"
                  className="px-10 py-3.5 rounded-lg bg-[#C6A14A] text-white text-base font-semibold tracking-wide hover:bg-[#b5923f] transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  View Packages
                </Link>
                <p className="text-xs text-gray-400 font-light tracking-wide">
                  24-hour delivery • Packages from $199
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════════════════════════
            SECTION 1: SIGNATURE REAL ESTATE PHOTOGRAPHY
            ════════════════════════════════════════════════════════════════════ */}
        <section className="py-8 md:py-12 px-6 overflow-hidden">
          <div className="max-w-7xl mx-auto">
            {/* Editorial Panel */}
            <div className="bg-white rounded-2xl border border-black/5 shadow-sm overflow-hidden">
              <div className="grid lg:grid-cols-12 items-center">
                
                {/* Image Column (60%) */}
                <div className="lg:col-span-7 fade-on-scroll opacity-0 translate-y-8 transition-all duration-700">
                  <div className="relative overflow-hidden aspect-[4/3] lg:aspect-auto lg:h-full">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_page-builder-81/artifacts/q25kwdyr_Adero_Canyon_Catteau_BKElevation_3301.jpg"
                      alt="Signature Real Estate Photography"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Text Column (40%) */}
                <div className="lg:col-span-5 p-8 md:p-10 lg:p-12 fade-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-150">
                  <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
                    Photography
                  </p>
                  <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-gray-900 mb-5 leading-tight">
                    Signature Real Estate Photography
                  </h2>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-6">
                    Every frame tells a story of luxury and possibility. Our signature photography captures the architectural elegance and emotional resonance that transforms listings into coveted properties.
                  </p>
                  
                  {/* Feature highlights */}
                  <ul className="space-y-2.5 mb-6">
                    <li className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C6A14A]"></span>
                      <span className="text-gray-500 text-sm">HDR & twilight photography</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C6A14A]"></span>
                      <span className="text-gray-500 text-sm">Magazine-quality composition</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C6A14A]"></span>
                      <span className="text-gray-500 text-sm">24-hour delivery</span>
                    </li>
                  </ul>

                  {/* Dual CTA Row */}
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 items-stretch sm:items-center mt-8">
                    <Link 
                      to="/pricing/real-estate"
                      data-testid="real-estate-cta"
                      className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 h-12 rounded-xl bg-[#C6A14A] text-white font-semibold tracking-wide hover:bg-[#b5923f] hover:-translate-y-0.5 transition-all duration-300 shadow-lg hover:shadow-xl"
                    >
                      View Pricing
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                    <button
                      onClick={() => setActiveModal('real_estate')}
                      data-testid="real-estate-consultation-cta"
                      className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 h-12 rounded-xl border border-[#C6A14A]/40 text-[#1a2636] font-semibold tracking-wide hover:border-[#C6A14A] hover:bg-[#C6A14A]/10 hover:-translate-y-0.5 transition-all duration-300"
                    >
                      Request a Quote
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════════════════════════
            SECTION 2: AERIAL & DRONE MEDIA
            ════════════════════════════════════════════════════════════════════ */}
        <section className="py-8 md:py-12 px-6 overflow-hidden">
          <div className="max-w-7xl mx-auto">
            {/* Editorial Panel */}
            <div className="bg-white rounded-2xl border border-black/5 shadow-sm overflow-hidden">
              <div className="grid lg:grid-cols-12 items-center">
                
                {/* Text Column (40%) - Left side */}
                <div className="lg:col-span-5 order-2 lg:order-1 p-8 md:p-10 lg:p-12 fade-on-scroll opacity-0 translate-y-8 transition-all duration-700">
                  <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
                    Aerial Media
                  </p>
                  <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-gray-900 mb-5 leading-tight">
                    Drone & Aerial Photography
                  </h2>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-6">
                    Elevate your perspective with cinematic aerial footage that showcases properties in their full grandeur. From sweeping estate views to neighborhood context — we capture the complete picture.
                  </p>
                  
                  {/* Feature highlights */}
                  <ul className="space-y-2.5 mb-6">
                    <li className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C6A14A]"></span>
                      <span className="text-gray-500 text-sm">Fully insured operations</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C6A14A]"></span>
                      <span className="text-gray-500 text-sm">4K cinematic quality</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C6A14A]"></span>
                      <span className="text-gray-500 text-sm">Property & neighborhood context</span>
                    </li>
                  </ul>

                  {/* Dual CTA Row */}
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 items-stretch sm:items-center mt-8">
                    <Link 
                      to="/services/aerial-photos-video"
                      data-testid="aerial-cta"
                      className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 h-12 rounded-xl bg-[#C6A14A] text-white font-semibold tracking-wide hover:bg-[#b5923f] hover:-translate-y-0.5 transition-all duration-300 shadow-lg hover:shadow-xl"
                    >
                      More Info
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                    <button
                      onClick={() => setActiveModal('aerial')}
                      data-testid="aerial-consultation-cta"
                      className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 h-12 rounded-xl border border-[#C6A14A]/40 text-[#1a2636] font-semibold tracking-wide hover:border-[#C6A14A] hover:bg-[#C6A14A]/10 hover:-translate-y-0.5 transition-all duration-300"
                    >
                      Request a Quote
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Image Column (60%) - Right side */}
                <div className="lg:col-span-7 order-1 lg:order-2 fade-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-150">
                  <div className="relative overflow-hidden aspect-[4/3] lg:aspect-auto lg:h-full">
                    <img 
                      src="https://images.unsplash.com/photo-1635111031688-9b13c0125d12?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjByZWFsJTIwZXN0YXRlJTIwcGhvdG9ncmFwaHklMjBhZXJpYWwlMjBkcm9uZSUyMHByb3BlcnR5fGVufDB8fHx8MTc3MDk0NTE3N3ww&ixlib=rb-4.1.0&q=85"
                      alt="Aerial drone photography of luxury property"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════════════════════════
            SECTION 3: HEADSHOTS & PORTRAITS
            ════════════════════════════════════════════════════════════════════ */}
        <section className="py-8 md:py-12 px-6 overflow-hidden">
          <div className="max-w-7xl mx-auto">
            {/* Editorial Panel */}
            <div className="bg-white rounded-2xl border border-black/5 shadow-sm overflow-hidden">
              <div className="grid lg:grid-cols-12 items-center">
                
                {/* Image Column (60%) - Left side */}
                <div className="lg:col-span-7 fade-on-scroll opacity-0 translate-y-8 transition-all duration-700">
                  <div className="relative overflow-hidden aspect-[4/3] lg:aspect-auto lg:h-full">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_page-builder-81/artifacts/q5ry1pzv_openart-image_4bPihNQc_1770259632182_raw.jpg"
                      alt="Professional headshots and portrait photography"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Text Column (40%) - Right side */}
                <div className="lg:col-span-5 p-8 md:p-10 lg:p-12 fade-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-150">
                  <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
                    Portraits
                  </p>
                  <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-gray-900 mb-5 leading-tight">
                    Professional Headshots & Portraits
                  </h2>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-6">
                    First impressions matter. From agent headshots to executive portraits, we craft images that command attention and communicate professionalism.
                  </p>
                  
                  {/* Feature highlights */}
                  <ul className="space-y-2.5 mb-6">
                    <li className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C6A14A]"></span>
                      <span className="text-gray-500 text-sm">Studio & on-location</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C6A14A]"></span>
                      <span className="text-gray-500 text-sm">Professional retouching</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C6A14A]"></span>
                      <span className="text-gray-500 text-sm">Brand-aligned styling</span>
                    </li>
                  </ul>

                  {/* Dual CTA Row */}
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 items-stretch sm:items-center mt-8">
                    <Link 
                      to="/services/headshots-portraits"
                      data-testid="headshots-cta"
                      className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 h-12 rounded-xl bg-[#C6A14A] text-white font-semibold tracking-wide hover:bg-[#b5923f] hover:-translate-y-0.5 transition-all duration-300 shadow-lg hover:shadow-xl"
                    >
                      More Info
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                    <button
                      onClick={() => setActiveModal('headshots')}
                      data-testid="headshots-consultation-cta"
                      className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 h-12 rounded-xl border border-[#C6A14A]/40 text-[#1a2636] font-semibold tracking-wide hover:border-[#C6A14A] hover:bg-[#C6A14A]/10 hover:-translate-y-0.5 transition-all duration-300"
                    >
                      Request a Quote
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════════════════════════
            OTHER SERVICES SECTION - AI/Digital, Video, Restoration, 3D Printing
            ════════════════════════════════════════════════════════════════════ */}
        <section className="py-12 md:py-16 px-6">
          <div className="max-w-[1200px] mx-auto">
            {/* Section Header */}
            <div className="text-center mb-10 fade-on-scroll opacity-0 translate-y-8 transition-all duration-700">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="w-10 h-px bg-[#C6A14A]"></div>
                <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.3em] uppercase">
                  Other Services
                </p>
                <div className="w-10 h-px bg-[#C6A14A]"></div>
              </div>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-gray-900 mb-3">
                Beyond Real Estate
              </h2>
              <p className="text-gray-600 text-base max-w-2xl mx-auto">
                Additional creative and digital services for businesses and individuals.
              </p>
            </div>

            {/* Services Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              
              {/* AI Influencer / Digital Persona */}
              <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6 hover:shadow-lg hover:border-[#C6A14A]/30 transition-all duration-300 group">
                <div className="aspect-[16/10] rounded-lg overflow-hidden mb-5 bg-gray-100">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_33a25cbc-45a3-405f-bc98-5937cf63a0ef/artifacts/nvrv9ry4_023.jpg"
                    alt="AI Influencer Development"
                    className="w-full h-full object-cover object-top"
                    data-testid="emma-portrait"
                  />
                </div>
                <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.2em] uppercase mb-2">
                  AI & Digital Personas
                </p>
                <h3 className="font-serif text-xl text-gray-900 mb-3">
                  Digital Persona Development
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-5">
                  Build a consistent on-camera identity with cinematic visuals and repeatable content workflows.
                </p>
                <Link 
                  to="/services/digital-persona-development"
                  data-testid="flagship-persona-cta"
                  className="inline-flex items-center gap-2 text-[#C6A14A] hover:text-[#b5923f] text-sm font-medium transition-colors"
                >
                  Learn More
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {/* Video & Brand Content */}
              <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6 hover:shadow-lg hover:border-[#C6A14A]/30 transition-all duration-300 group">
                <div className="aspect-[16/10] rounded-lg overflow-hidden mb-5 bg-gray-100 flex items-center justify-center">
                  <Video className="w-12 h-12 text-gray-300" />
                </div>
                <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.2em] uppercase mb-2">
                  Video Production
                </p>
                <h3 className="font-serif text-xl text-gray-900 mb-3">
                  Video & Brand Content
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-5">
                  Cinematic video tours, social media content, and brand storytelling for businesses.
                </p>
                <Link 
                  to="/pricing/video-brand"
                  className="inline-flex items-center gap-2 text-[#C6A14A] hover:text-[#b5923f] text-sm font-medium transition-colors"
                >
                  View Pricing
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {/* AI Systems & Automation */}
              <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6 hover:shadow-lg hover:border-[#C6A14A]/30 transition-all duration-300 group">
                <div className="aspect-[16/10] rounded-lg overflow-hidden mb-5 bg-gray-100">
                  <img 
                    src="https://customer-assets-lqy194kg.emergentagent.net/job_42e529fb-0c64-4c59-a1a2-25a0eb5bcf7b/artifacts/lbi5i5zq_image.png"
                    alt="AI Systems & Automation"
                    className="w-full h-full object-cover"
                    data-testid="homepage-kpops-image"
                  />
                </div>
                <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.2em] uppercase mb-2">
                  Business Automation
                </p>
                <h3 className="font-serif text-xl text-gray-900 mb-3">
                  AI Systems & Automation
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-5">
                  Custom operational systems to help businesses automate workflows and manage data.
                </p>
                <Link 
                  to="/services/ai-systems-automation"
                  data-testid="homepage-ai-systems-link"
                  className="inline-flex items-center gap-2 text-[#C6A14A] hover:text-[#b5923f] text-sm font-medium transition-colors"
                >
                  Learn More
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {/* Photo Restoration */}
              <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6 hover:shadow-lg hover:border-[#C6A14A]/30 transition-all duration-300 group">
                <div className="aspect-[16/10] rounded-lg overflow-hidden mb-5 bg-gray-100">
                  <BeforeAfterSlider
                    beforeSrc={restorationImages.before}
                    afterSrc={restorationImages.after}
                    altBefore="Original faded photo"
                    altAfter="Restored and enhanced photo"
                    initialPercent={50}
                  />
                </div>
                <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.2em] uppercase mb-2">
                  Photo Services
                </p>
                <h3 className="font-serif text-xl text-gray-900 mb-3">
                  Photo Restoration & Enhancement
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-5">
                  Breathe new life into damaged, faded, or vintage photographs with AI-powered restoration.
                </p>
                <Link 
                  to="/services/photo-restoration-enhancement"
                  data-testid="restoration-cta"
                  className="inline-flex items-center gap-2 text-[#C6A14A] hover:text-[#b5923f] text-sm font-medium transition-colors"
                >
                  Learn More
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {/* 3D Printing */}
              <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6 hover:shadow-lg hover:border-[#C6A14A]/30 transition-all duration-300 group">
                <div className="aspect-[16/10] rounded-lg overflow-hidden mb-5 bg-gray-100 flex items-center justify-center">
                  <Sparkles className="w-12 h-12 text-gray-300" />
                </div>
                <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.2em] uppercase mb-2">
                  Custom Fabrication
                </p>
                <h3 className="font-serif text-xl text-gray-900 mb-3">
                  3D Printing Services
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-5">
                  Custom 3D printed models, prototypes, and architectural scale models for presentations.
                </p>
                <Link 
                  to="/contact"
                  className="inline-flex items-center gap-2 text-[#C6A14A] hover:text-[#b5923f] text-sm font-medium transition-colors"
                >
                  Inquire
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {/* Portraits (link to pricing) */}
              <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6 hover:shadow-lg hover:border-[#C6A14A]/30 transition-all duration-300 group">
                <div className="aspect-[16/10] rounded-lg overflow-hidden mb-5 bg-gray-100 flex items-center justify-center">
                  <Users className="w-12 h-12 text-gray-300" />
                </div>
                <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.2em] uppercase mb-2">
                  Portrait Photography
                </p>
                <h3 className="font-serif text-xl text-gray-900 mb-3">
                  Personal & Family Portraits
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-5">
                  Couples, families, pets, and personal portrait sessions with professional styling.
                </p>
                <Link 
                  to="/pricing/portraits"
                  className="inline-flex items-center gap-2 text-[#C6A14A] hover:text-[#b5923f] text-sm font-medium transition-colors"
                >
                  View Pricing
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════════════════════════════════
            LUXURY CALL-TO-ACTION - Full-width accent
            ════════════════════════════════════════════════════════════════════ */}
        <section className="relative bg-gray-900 py-10 md:py-14 overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute inset-0 pointer-events-none">
            <div 
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] opacity-20"
              style={{ background: 'radial-gradient(ellipse at center, rgba(198, 161, 74, 0.3) 0%, transparent 60%)' }}
            />
          </div>

          <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
            {/* Decorative line */}
            <div className="w-12 h-[1px] bg-[#C6A14A]/40 mx-auto mb-5 fade-on-scroll opacity-0 translate-y-8 transition-all duration-700"></div>
            
            <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.3em] uppercase mb-4 fade-on-scroll opacity-0 translate-y-8 transition-all duration-700">
              Ready to Elevate Your Listings?
            </p>
            
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-white mb-4 fade-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-100">
              Serving Kingman, Bullhead City, Lake Havasu & Mohave County
            </h2>
            
            <p className="text-white/60 text-base md:text-lg mb-6 max-w-2xl mx-auto fade-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-200">
              From stunning photography to immersive video tours, we provide the premium visual assets that transform listings into sold properties.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center fade-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-300">
              <Link 
                to="/book"
                data-testid="cta-order-services"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg bg-[#C6A14A] text-white font-semibold tracking-wide hover:bg-[#b5923f] transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book a Shoot
                <ChevronRight className="w-4 h-4" />
              </Link>
              <PhonePopover variant="hero-outline" data-testid="cta-contact">
                Call (928) 530-6604
              </PhonePopover>
            </div>
            
            {/* Decorative line */}
            <div className="w-12 h-[1px] bg-[#C6A14A]/40 mx-auto mt-6 fade-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-400"></div>
          </div>
        </section>

      </div>{/* End white body container */}

      {/* Service Consultation Modal */}
      {activeModal && modalConfigs[activeModal] && (
        <ConsultationModal 
          isOpen={true}
          onClose={() => setActiveModal(null)}
          source={modalConfigs[activeModal].source}
          subject={modalConfigs[activeModal].subject}
          serviceLabel={modalConfigs[activeModal].serviceLabel}
        />
      )}
    </div>
  );
};

export default Home;
